import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronDown, Loader2 } from 'lucide-react'
import { BUSINESS_HEALTH_GROUPS, type BusinessHealthGroup } from '@/lib/businessHealthChecklist'
import { getPartnerBusinessHealthQuarter, type BusinessHealthQuarter } from '@/services/businessHealthService'

function moscowYearQuarter(): { year: number; quarter: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Moscow', year: 'numeric', month: 'numeric',
  }).formatToParts(new Date())
  const year = Number(parts.find(part => part.type === 'year')?.value)
  const month = Number(parts.find(part => part.type === 'month')?.value)
  return { year, quarter: Math.ceil(month / 3) }
}

function answerPoints(value: unknown): number {
  if (value === 'yes') return 1
  if (value === 'partial') return 0.5
  return 0
}

interface GroupScore {
  percent: number | null
  assessed: number
  total: number
  filled: number
  questions: number
}

function scoreGroup(group: BusinessHealthGroup, data: BusinessHealthQuarter): GroupScore {
  const sources = group.id === 'entrepreneur'
    ? [data.entrepreneur?.answers ?? null]
    : data.studios.map(studio => studio.answers)
  const assessed = sources.filter((source): source is Record<string, unknown> => source !== null)
  const filled = assessed.reduce((sum, answers) => sum + group.questions.filter(question =>
    answers[question.id] === 'yes' || answers[question.id] === 'no' || answers[question.id] === 'partial',
  ).length, 0)
  const points = assessed.reduce((sum, answers) => sum + group.questions.reduce(
    (groupSum, question) => groupSum + answerPoints(answers[question.id]), 0,
  ), 0)
  return {
    percent: assessed.length > 0 ? Math.round(points / (assessed.length * group.questions.length) * 100) : null,
    assessed: assessed.length,
    total: sources.length,
    filled,
    questions: assessed.length * group.questions.length,
  }
}

function scoreTone(percent: number | null) {
  if (percent === null) return { label: 'Нет оценки', text: 'text-muted-foreground', bar: 'bg-muted-foreground', badge: 'bg-muted text-muted-foreground' }
  if (percent >= 80) return { label: 'В норме', text: 'text-emerald-600 dark:text-emerald-400', bar: 'bg-emerald-500', badge: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' }
  if (percent >= 50) return { label: 'Требует внимания', text: 'text-amber-600 dark:text-amber-400', bar: 'bg-amber-500', badge: 'bg-amber-500/10 text-amber-700 dark:text-amber-300' }
  return { label: 'Критично', text: 'text-rose-600 dark:text-rose-400', bar: 'bg-rose-500', badge: 'bg-rose-500/10 text-rose-700 dark:text-rose-300' }
}

function formatDate(value: string | null): string {
  if (!value) return 'нет оценки'
  const date = new Date(`${value}T12:00:00`)
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium' }).format(date)
}

export function PartnerBusinessHealthPage() {
  const { partnerNumber } = useParams<{ partnerNumber: string }>()
  const parsedPartnerNumber = Number(partnerNumber)
  const current = useMemo(moscowYearQuarter, [])
  const [year, setYear] = useState(current.year)
  const [quarter, setQuarter] = useState(current.quarter)
  const [data, setData] = useState<BusinessHealthQuarter | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!Number.isSafeInteger(parsedPartnerNumber) || parsedPartnerNumber <= 0) {
      setError('Не удалось определить франчайзи.')
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    void getPartnerBusinessHealthQuarter(parsedPartnerNumber, year, quarter)
      .then(result => { if (!cancelled) setData(result) })
      .catch(cause => { if (!cancelled) { setData(null); setError(cause?.message || 'Не удалось загрузить аналитику.') } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [parsedPartnerNumber, year, quarter])

  const scores = useMemo(() => data ? BUSINESS_HEALTH_GROUPS.map(group => ({ group, score: scoreGroup(group, data) })) : [], [data])
  const assessedScores = scores.map(item => item.score.percent).filter((value): value is number => value !== null)
  const overall = assessedScores.length === BUSINESS_HEALTH_GROUPS.length
    ? Math.round(assessedScores.reduce((sum, value) => sum + value, 0) / BUSINESS_HEALTH_GROUPS.length)
    : null
  const overallTone = scoreTone(overall)
  const assessedStudios = data?.studios.filter(studio => studio.answers !== null).length ?? 0
  const yearOptions = Array.from({ length: Math.max(1, current.year - 2025) }, (_, index) => current.year - index)

  return (
    <main className="mx-auto w-full max-w-6xl space-y-5 px-4 py-6 pb-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Здоровье бизнеса</h1>
          <p className="mt-1 text-sm text-muted-foreground">Сводная оценка по всем доступным студиям франчайзи. Блок «Предприниматель» оценивается один раз после миграции.</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label htmlFor="business-health-year" className="text-muted-foreground">Год</label>
          <select id="business-health-year" value={year} onChange={event => setYear(Number(event.target.value))}
            className="rounded-lg border border-border bg-card px-3 py-2 text-foreground">
            {yearOptions.map(value => <option key={value} value={value}>{value}</option>)}
          </select>
          <label htmlFor="business-health-quarter" className="ml-2 text-muted-foreground">Квартал</label>
          <select id="business-health-quarter" value={quarter} onChange={event => setQuarter(Number(event.target.value))}
            className="rounded-lg border border-border bg-card px-3 py-2 text-foreground">
            {[1, 2, 3, 4].map(value => <option key={value} value={value}>{value}</option>)}
          </select>
        </div>
      </div>

      {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Загружаем аналитику…</div>}
      {error && <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">{error}</div>}
      {!loading && data && (
        <>
          {data.storage_scope === 'legacy_studio' && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
              Предварительный просмотр: миграция ещё не применена. Студийные оценки доступны; общий блок «Предприниматель» и полный итог появятся после неё.
            </div>
          )}
          <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-5">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Общее здоровье бизнеса</h2>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <span className={`text-5xl font-bold tabular-nums ${overallTone.text}`}>{overall === null ? '—' : `${overall}%`}</span>
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${overallTone.badge}`}>{overallTone.label}</span>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">Оценены {assessedStudios} из {data.studios.length} студий · {assessedScores.length} из 6 блоков. Общий процент появится после оценки всех блоков.</p>
              </div>
              <div className="flex h-32 w-32 items-center justify-center rounded-full"
                style={{ background: `conic-gradient(${overall === null ? 'hsl(var(--muted-foreground))' : overall >= 80 ? '#10b981' : overall >= 50 ? '#f59e0b' : '#ef4444'} ${overall ?? 0}%, hsl(var(--muted)) 0)` }}>
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-card text-2xl font-semibold text-foreground">{overall === null ? '—' : `${overall}%`}</div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">Здоровье по блокам</h2>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {scores.map(({ group, score }) => {
                const tone = scoreTone(score.percent)
                return (
                  <details key={group.id} className="group rounded-xl border border-border bg-card p-4 shadow-sm">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
                      <div>
                        <p className="text-base font-semibold text-foreground">{group.title}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{group.id === 'entrepreneur' ? 'Общий блок франчайзи' : `${score.assessed} из ${score.total} студий`}</p>
                      </div>
                      <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="mt-3 flex items-center gap-2">
                      <span className={`text-4xl font-bold tabular-nums ${tone.text}`}>{score.percent === null ? '—' : `${score.percent}%`}</span>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${tone.badge}`}>{tone.label}</span>
                    </div>
                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted"><div className={`h-full rounded-full ${tone.bar}`} style={{ width: `${score.percent ?? 0}%` }} /></div>
                    <p className="mt-3 text-xs leading-5 text-muted-foreground">{group.description}</p>
                    <p className="mt-2 text-xs text-muted-foreground">Заполнено {score.filled} из {score.questions} вопросов в оценённых {group.id === 'entrepreneur' ? 'ответах' : 'студиях'}.</p>
                    <div className="mt-3 border-t border-border pt-3">
                      {group.id === 'entrepreneur' ? (
                        <p className="text-xs text-muted-foreground">Последняя оценка: {formatDate(data.entrepreneur?.week_start ?? null)}. Общие ответы видны в чек-листе каждой студии.</p>
                      ) : (
                        <ul className="space-y-2">
                          {data.studios.map(studio => {
                            const points = studio.answers ? group.questions.reduce((sum, question) => sum + answerPoints(studio.answers?.[question.id]), 0) : null
                            const percent = points === null ? null : Math.round(points / group.questions.length * 100)
                            return (
                              <li key={studio.studio_internal_id} className="flex items-center justify-between gap-2 text-xs">
                                <Link className="text-primary hover:underline" to={`/partners/${parsedPartnerNumber}/studios/${studio.studio_internal_id}/business-health`}>Студия {studio.studio_internal_id}</Link>
                                <span className="text-muted-foreground">{percent === null ? 'Нет оценки' : `${percent}% · ${formatDate(studio.week_start)}`}</span>
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </div>
                  </details>
                )
              })}
            </div>
          </section>
        </>
      )}
    </main>
  )
}
